import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, JSX } from 'react';
import { Formik } from 'formik';
import { buildGroupCreationRows } from './buildGroupCreationRows.helper';
import { useLockedFeatures } from '../../../providers/LockedFeatures.provider';
import { useLockedFeatureDialogue } from '../../../providers/LockedFeatureDialogue.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useTranslation } from 'react-i18next';

const { Box, Button, Table, TextField, Select, Icon } = vp;

type GroupCreationProps = {
  handlers: {
    onGroupCreation: (values: vp.CreateGroupRequest) => void;
  };
};

type FC = FunctionComponent<GroupCreationProps>;

const GroupCreationForm: FC = (props: GroupCreationProps) => {
  const { handlers } = props;
  const {
    response: { Country },
  } = useClientData();
  const groupCreationRow = buildGroupCreationRows(Country);
  const lockedFeatures = useLockedFeatures();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const { t } = useTranslation('translations', { keyPrefix: 'Groups.Create.Form' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const defaultValues = {
    GroupName: '',
    Description: '',
    Service: '',
    Currency: Country === 'US' ? 'USD' : 'CAD',
  };

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={handlers.onGroupCreation}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit }) => {
        const groupCreationTable: vp.TableProps = {
          name: 'group-creation-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: groupCreationRow.map(({ label, id, require, preset, type, placeholder, getSelectOptions }) => ({
            Field: (
              <Box ai="center">
                {label} {getRequiredIcon(require)}
              </Box>
            ),
            Input:
              type === 'input' ? (
                <TextField
                  preset={preset}
                  key={id}
                  name={id}
                  variant="standard"
                  placeholder={placeholder}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={errors[id] && touched[id] ? errors[id] : null}
                />
              ) : (
                <Select
                  variant="standard"
                  options={getSelectOptions()}
                  lockedValues={lockedFeatures.LockedSelectOptions}
                  lockedCallback={(e: string) => updateIsLocked(true, e)}
                  key={id}
                  name={id}
                  value={String(values[id])}
                  placeholder={placeholder}
                  noOrdering
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={errors[id] && touched[id] ? errors[id] : null}
                />
              ),
          })),
        };

        return (
          <form onSubmit={handleSubmit}>
            <Table {...groupCreationTable} />
            <Box mt="md" jc="fe">
              <Button id="group-creation-submit" label={tGlobal('Buttons.create')} type="submit" />
            </Box>
          </form>
        );
      }}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      GroupName: Yup.string().required().max(50, t('Validation.groupNameLength')),
      Description: Yup.string().required().max(200, t('Validation.descriptionLength')),
      Service: Yup.string().required(),
      Currency: Yup.string().required(),
    });
  }
  function getRequiredIcon(isRequired: boolean): JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
};

export default GroupCreationForm;
